import { Trash } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { getValueList } from '../../../../../sources/value';
import translations from '../../../../../translations';
import type { Listener } from '../../../../../types/graph';
import type { ValueElementIdAPI, ValueElementIdReferenceAPI } from '../../../../../types/value';
import { isNullOrEmpty } from '../../../../../utils/guard';
import ButtonPrimary from '../../../../buttons/ButtonPrimary';
import DraggableTable from '../../../../generic/DraggableTable';
import Glyphicon from '../../../../generic/Glyphicon';
import type { TableColumnList } from '../../../../generic/Table';
import { useNotificationDispatch } from '../../../../notifications/NotificationProvider';
import { getValueName } from '../../../../values/selector/value-selector-utils';
import { useListener } from '../../contextProviders/ListenerProvider';
import { useMapElement } from '../../contextProviders/MapElementProvider';

const ListenerList = () => {
    const [valueReferences, setValueReferences] = useState<ValueElementIdReferenceAPI[]>([]);

    const { mapElement, setMapElement } = useMapElement();
    const { onCreateListener, onEditListener, onDeleteListener } = useListener();
    const dispatchNotification = useNotificationDispatch();

    const onReorderListeners = (listeners: Listener[]) => {
        setMapElement({
            ...mapElement,
            listeners: listeners,
        });
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        if (!Array.isArray(mapElement?.listeners)) {
            return;
        }

        // Get the list of value reference ids from the items so we can request just those value references
        const valueReferenceIds: string[] = mapElement.listeners.reduce(
            (idList: string[], listener) => {
                if (
                    listener.valueElementToReferenceForListeningId !== null &&
                    listener.valueElementToReferenceForListeningId.id !== null
                ) {
                    idList.push(listener.valueElementToReferenceForListeningId.id);
                }
                return idList;
            },
            [],
        );

        if (valueReferenceIds.length === 0) {
            return;
        }

        const filter = { id: valueReferenceIds };

        (async () => {
            try {
                const response = await getValueList(filter);

                setValueReferences(response);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        })();
    }, []);

    const getValueCellText = (itemReferenceId: ValueElementIdAPI) => {
        if (isNullOrEmpty(itemReferenceId)) {
            return null;
        }

        const valueReference = valueReferences.find(
            (valueReference) =>
                valueReference.id === itemReferenceId.id &&
                // typeElementPropertyId can be either null or undefined
                (valueReference?.typeElementPropertyId ?? null) ===
                    (itemReferenceId?.typeElementPropertyId ?? null),
        );

        if (!valueReference) {
            return null;
        }

        return getValueName(valueReference);
    };

    const columns: TableColumnList<Listener> = [
        {
            renderHeader: () => translations.COMMON_TABLE_name,
            renderCell: ({ item, rowIndex }) => (
                <button
                    className="link-emulate"
                    title={`Edit ${item.developerName}`}
                    onClick={() => onEditListener(item, rowIndex)}
                    type="button"
                >
                    {item.developerName ?? ''}
                </button>
            ),
        },
        {
            renderHeader: () => translations.COMMON_TABLE_listener_type,
            renderCell: ({ item: { listenerType } }) => listenerType ?? '',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_value,
            renderCell: ({ item: { valueElementToReferenceForListeningId } }) =>
                valueElementToReferenceForListeningId
                    ? getValueCellText(valueElementToReferenceForListeningId)
                    : '',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_actions,
            renderCell: ({ item, rowIndex }) => (
                <div className="action-btn-wrapper">
                    <button
                        title={`Delete ${item.developerName}`}
                        className="table-icon table-icon-delete margin-left"
                        aria-label={`Delete ${item.developerName}`}
                        onClick={() => onDeleteListener(rowIndex)}
                        type="button"
                    >
                        <Trash />
                    </button>
                </div>
            ),
            size: '7.5rem',
        },
    ];

    return (
        <>
            <h4>Listeners</h4>
            <div className="margin-bottom flex">
                <ButtonPrimary onClick={onCreateListener}>
                    <Glyphicon glyph="plus" />
                    Add Listener
                </ButtonPrimary>
            </div>
            <DraggableTable
                columns={columns}
                wrapperClassName="margin-bottom-large"
                items={mapElement.listeners ?? []}
                testId="listener-list"
                tableId="listener-list"
                onReorder={onReorderListeners}
            />
        </>
    );
};

export default ListenerList;
